import React, { useContext, useEffect, useRef, useState } from 'react'
import Heading from '@components/Heading'
import { HEADING_TYPE, DIVIDER_SIZE, BUTTON_TYPE } from '@helpers/constants'
import Divider from '@components/Divider'
import Button from '@components/Button'
import { nanoid } from 'nanoid'
import { renderToStaticMarkup } from 'react-dom/server'
import { connectToEditorjs } from '@blocks/connectToEditorjs'
import Icon from '@components/Icon'
import Form from '@components/Form'
import EditExerciseWrapper from '@components/EditExerciseWrapper'
import { addOnSaveCallback } from '@state/actions/editorActions'
import { AppContext } from '@state/context'
import { getImageSrc } from '@helpers/utilities'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'

/**
 * DragWordToImage
 * @returns
 */
const DragWordToImageComponent = ({
  initialState,
  handleStateChange,
  onSave,
  id,
}) => {
  const [state, setState] = useState(initialState)
  const { name, preamble, image, wordPairs = [] } = state
  const [_, dispatch] = useContext(AppContext)

  useEffect(() => {
    dispatch(
      addOnSaveCallback({
        id,
        callback: async () => {
          let newImage = await (async () => {
            if (!(image?.previewUrl instanceof File)) return image
            const filename = await uploadImage(image.previewUrl)

            return { filename: `${storagePath}/${filename}` }
          })()

          const newWords = await Promise.all(
            wordPairs?.map(async (pair) => {
              if (!(pair?.image?.previewUrl instanceof File)) return pair
              const fileName = await uploadImage(pair.image.previewUrl)

              return {
                ...pair,
                image: {
                  filename: `${storagePath}/${fileName}`,
                  id: pair.image.id,
                },
              }
            })
          )

          handleStateChange({
            ...state,
            image: newImage,
            wordPairs: newWords,
          })
        },
      })
    )
  }, [state])

  /**
   * Broadcast state change up
   */
  useEffect(() => {
    handleStateChange(state)
  }, [state])

  /**
   * Remove question
   * @param {*} pair
   */
  const removePair = (pair) => {
    setState({
      ...state,
      wordPairs: wordPairs.filter((q, k) => k !== pair),
    })
  }

  const movePair = (fromIndex, toIndex) => {
    const element = wordPairs[fromIndex]
    wordPairs.splice(fromIndex, 1)
    wordPairs.splice(toIndex, 0, element)

    setState({
      ...state,
      wordPairs: [...wordPairs],
    })
  }

  /**
   * Add new question
   */
  const addNewPair = () => {
    setState({
      ...state,
      wordPairs: [
        ...wordPairs,
        {
          word: '',
          image: {
            id: nanoid(),
            previewUrl: null,
          },
        },
      ],
    })
  }

  /**
   * Update question
   * @param {*} pairKey
   * @param {*} key
   * @param {*} value
   */
  const updatePair = (pairKey, attribute, value = false) => {
    const newPairs = wordPairs.map((pair, pKey) => {
      if (pairKey === pKey) {
        if (attribute === 'image') {
          let newObject = Object.assign({}, pair[attribute])
          newObject.previewUrl = value
          delete newObject.filename

          pair[attribute] = newObject
        } else {
          pair[attribute] = value
        }
      }
      return pair
    })

    setState({
      ...state,
      wordPairs: newPairs,
    })
  }

  return (
    <div>
      <EditExerciseWrapper type={'Dra ord till rätt bild'} onSave={onSave}>
        {/* ----- Name ----- */}
        <Heading headingLevel={HEADING_TYPE.H3}>Namn på övningen</Heading>
        <Form.Editor
          content={name}
          handleChange={(value) => setState({ ...state, name: value })}
          elementId={`word-to-image-name-${id}`}
        />
        <Divider size={DIVIDER_SIZE.SMALL} />
        <Heading headingLevel={HEADING_TYPE.H3}>Inledande text</Heading>
        <Form.Editor
          content={preamble}
          handleChange={(value) => setState({ ...state, preamble: value })}
          // placeholder={'Skriv en inledande text om övningen'}
          elementId={`word-to-image-preamble-${id}`}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Heading headingLevel={HEADING_TYPE.H3}>Bild</Heading>
        <Form.ImageInput
          handleImageChange={(e) => {
            e.preventDefault()
            setState({
              ...state,
              image: { previewUrl: e.target.files[0] },
            })
          }}
          id={'quiz-image'}
          imagePreviewUrl={getImageSrc(image)}
          inputKey={'inputKey'}
          name={'image'}
          onDelete={() => {
            setState({ ...state, image: null })
          }}
        />
        <Divider size={DIVIDER_SIZE.SMALL} />

        {/* ----- Pairs ----- */}
        <ul>
          {wordPairs &&
            wordPairs.map((pair, pairKey) => {
              if (!pair.id) {
                updatePair(pairKey, 'id', nanoid())
              }

              return (
                <li key={pair.id} className={'mb-5'}>
                  <Heading headingLevel={HEADING_TYPE.H3}>
                    Bild {pairKey + 1}
                  </Heading>
                  <Divider size={DIVIDER_SIZE.LINE} line />
                  <Divider size={DIVIDER_SIZE.SMALL} />
                  <div className={'flex'}>
                    <div className={'mr-5 flex-shrink-0'}>
                      <Form.ImageInput
                        handleImageChange={(e) => {
                          e.preventDefault()
                          updatePair(pairKey, 'image', e.target.files[0])
                        }}
                        id={pairKey}
                        imagePreviewUrl={getImageSrc(pair.image)}
                        inputKey={pairKey}
                        name={'image'}
                        onDelete={() => {
                          updatePair(pairKey, 'image', null)
                        }}
                      />
                    </div>

                    <div className={'w-full'}>
                      <p>Fyll i ordet som passar till bilden</p>
                      <Form.Input
                        type={'text'}
                        defaultValue={pair.word}
                        onChange={(e) =>
                          updatePair(pairKey, 'word', e.target.value)
                        }
                      />
                    </div>
                  </div>

                  <Divider size={DIVIDER_SIZE.SMALL} />

                  <Button
                    type={BUTTON_TYPE.PRIMARY}
                    handleClick={() => removePair(pairKey)}
                    marginRight
                  >
                    Ta bort bild
                  </Button>
                  <Button
                    type={BUTTON_TYPE.PRIMARY}
                    disabled={pairKey === 0}
                    handleClick={() => movePair(pairKey, pairKey - 1)}
                    marginRight
                  >
                    Flytta upp
                  </Button>
                  <Button
                    type={BUTTON_TYPE.PRIMARY}
                    disabled={pairKey + 1 === wordPairs.length}
                    handleClick={() => movePair(pairKey, pairKey + 1)}
                  >
                    Flytta ner
                  </Button>

                  <Divider
                    size={
                      pairKey + 1 === wordPairs.length
                        ? DIVIDER_SIZE.MEDIUM
                        : DIVIDER_SIZE.SMALL
                    }
                  />
                </li>
              )
            })}
        </ul>
        {/* ----- Button ----- */}
        <Button handleClick={addNewPair} type={BUTTON_TYPE.PRIMARY}>
          Lägg till en bild
        </Button>
        <Divider size={DIVIDER_SIZE.MEDIUM} />
      </EditExerciseWrapper>
    </div>
  )
}

export const DragWordToImage = connectToEditorjs(
  DragWordToImageComponent,
  'Dra ord till rätt bild',
  renderToStaticMarkup(<Icon icon={'Eye'} />)
)
